/**
 * D01 标的身份贯通 —— 统一 instrument 身份模型。
 *
 * 现有代码中同一标的的「现货 / 永续 / 交易所」身份散落在 symbol、venue、
 * product、exchange 等多个字段，且多处链接只传 symbol，导致
 * 现货信号 → 行情页被解析成永续合约（V4 D01 复现）。
 *
 * 本模块提供唯一的事实来源：
 *  - normalizeExchange / normalizeProduct：别名归一（BINANCE→Binance、perp→futures）
 *  - parseInstrumentId：解析内部 `venue:product:symbol` 格式（信号/研究/预警使用）
 *  - buildCoinRoute：构建携带身份的行情路由（无身份时保持旧路径向后兼容）
 *  - resolveCoinIdentity：从路由 query 解析身份（旧路径默认 futures，明确提示）
 *  - instrumentKey：身份唯一键（自选/去重/会员判断使用）
 */

export type ProductKind = 'spot' | 'futures' | 'perp' | 'linear_perp' | 'inverse_perp';

export interface InstrumentIdentity {
  /** 行情页 symbol（如 AVAXUSDT / BTCUSDT），大写 */
  symbol: string;
  /** 交易所展示名（Binance / OKX / Gate / Bitget / Hyperliquid） */
  exchange?: string;
  /** 产品类型（spot / futures；内部原始值可为 perp/linear_perp/inverse_perp） */
  product?: ProductKind;
}

const EXCHANGE_ALIASES: Record<string, string> = {
  binance: 'Binance',
  okx: 'OKX',
  gate: 'Gate',
  bitget: 'Bitget',
  hyperliquid: 'Hyperliquid',
  hyper: 'Hyperliquid',
  hl: 'Hyperliquid',
  bybit: 'Bybit',
  coinbase: 'Coinbase',
  kraken: 'Kraken',
};

/** 归一化交易所名：BINANCE/BIANACE → Binance；未知值原样返回。 */
export function normalizeExchange(v?: string | null): string | undefined {
  if (!v) return undefined;
  const key = v.trim().toLowerCase();
  return EXCHANGE_ALIASES[key] ?? v.trim();
}

/** 归一化产品：perp/linear_perp/inverse_perp/合约/永续 → futures；spot/现货 → spot。 */
export function normalizeProduct(v?: string | null): ProductKind | undefined {
  if (!v) return undefined;
  const key = v.trim().toLowerCase();
  if (key === 'spot' || key === '现货') return 'spot';
  if (key === 'futures' || key === 'perp' || key === 'linear_perp' || key === 'inverse_perp' || key === '永续' || key === '合约' || key === 'contract') return 'futures';
  return undefined;
}

/**
 * 解析内部 instrumentId（`venue:product:symbol`，如 `binance:spot:AVAXUSDT`）。
 * 信号、历史验证、预警等模块使用该格式；无法解析返回 null。
 */
export function parseInstrumentId(id?: string | null): InstrumentIdentity | null {
  if (!id) return null;
  const parts = id.split(':');
  if (parts.length < 2) return null;
  // 兼容 `venue:product:symbol` 与旧 `venue:symbol`（缺 product 时视为永续）
  if (parts.length === 2) {
    return {
      symbol: parts[1].toUpperCase(),
      exchange: normalizeExchange(parts[0]),
      product: 'futures',
    };
  }
  return {
    symbol: parts.slice(2).join(':').toUpperCase(),
    exchange: normalizeExchange(parts[0]),
    product: normalizeProduct(parts[1]) ?? 'futures',
  };
}

/** 构建币种行情路由；有身份时携带 exchange/product query，无身份时保持旧路径。 */
export function buildCoinRoute(
  instr: InstrumentIdentity | string,
  extra?: Record<string, string>,
): string {
  const sym = typeof instr === 'string' ? instr.toUpperCase() : instr.symbol.toUpperCase();
  const q = new URLSearchParams();
  if (typeof instr !== 'string') {
    if (instr.exchange) q.set('exchange', instr.exchange);
    const product = normalizeProduct(instr.product);
    if (product) q.set('product', product);
  }
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v) q.set(k, v);
    }
  }
  const qs = q.toString();
  return qs ? `/coin/${sym}?${qs}` : `/coin/${sym}`;
}

/** 从路由 query（exchange/product/type）解析身份；旧路径默认 futures。 */
export function resolveCoinIdentity(
  symbol: string,
  q: { exchange?: string; product?: string; type?: string },
): InstrumentIdentity {
  const product = normalizeProduct(q.product) ?? (q.type === 'spot' ? 'spot' : 'futures');
  const exchange = normalizeExchange(q.exchange);
  return { symbol: symbol.toUpperCase(), exchange, product };
}

/** 身份唯一键：`SYMBOL|exchange|product`（默认 futures）。自选/去重/会员判断用。 */
export function instrumentKey(instr: InstrumentIdentity | string): string {
  if (typeof instr === 'string') return instr.toUpperCase();
  const product = normalizeProduct(instr.product) ?? 'futures';
  return `${instr.symbol.toUpperCase()}|${(instr.exchange ?? '').toLowerCase()}|${product}`;
}
